<i18n>
{
  "en": {
    "forecastDemand": "1. Forecast demand",
    "priceOptimizer": "2. Price optimizer",
    "menuEngineering": "3. Smart menu",
    "standaloneMenuEngineering": "2. Menu engineering",
    "menuPreview": "Menu preview",
    "noManagement": "No management actions available for this restaurant",
    "noPricing": "Price optimizer isn't available for this period. If you have any questions, ask Support.",
    "outOfMenu": "Out-of-menu products",
    "priceChange": "Price changes"
  },
  "es": {
    "forecastDemand": "1. Previsión de demandas",
    "priceOptimizer": "2. Optimizador de precios",
    "menuEngineering": "3. Menu inteligente",
    "standaloneMenuEngineering": "2. Ingeniería de menú",
    "menuPreview": "Visualización de menú",
    "noManagement": "No hay gestiones disponibles para este restaurante",
    "noPricing": "No está disponible el optimizador de precios para este periodo. Si tiene dudas, consulte a Soporte.",
    "outOfMenu": "Productos fuera de menu",
    "priceChange": "Cambios de precio"
  }
}
</i18n>

<template>
  <div>
    <v-layout row wrap justify-space-around>
      <dynameat-management-button v-for="button in buttonList" :key="button.action"
                                  :action="button.action" :title="button.title"
                                  :icon="button.icon" :show-badge="button.showBadge"
                                  :previous="isPreviousPeriod" :visible="button.visible"
                                  :disabled="button.disabled" v-on="$listeners" class="btn danger">
      </dynameat-management-button>
    </v-layout>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';

import { PredictionPeriod } from '@dynameat/views/pointOfSale/models';
import { createInfoNotification } from '@ulibs/unnotificationsqueue';
import DynameatManagementButton from '@dynameat/components/DynameatManagementButton';

import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import { PermissionController } from '@core/permissions';
import PriceProfitabilityChart, { SIZE_TYPES } from './PriceProfitabilityChart.vue';

const PERIOD_DEPENDENCY = 'period';
const PRICE_DEPENDENCY = 'price';
const MENU_DEPENDENCY = 'menu';
const EXPERIMENT_DEPENDENCY = 'experiment';
@Component({
  components: { PriceProfitabilityChart }
})
@Components({ DynameatManagementButton, PriceProfitabilityChart })
@MapActions(['addNotification'])
@MapGetters(['predictionPeriodList'])
export default class OptimizerPanel extends Vue {
  @Prop({
    Object,
    validator(period) {
      return period instanceof PredictionPeriod;
    }
  }) currentPeriod;

  get chartSizeType() {
    return SIZE_TYPES.SMALL;
  }

  get isPreviousPeriod() {
    return this.currentPeriod?.isPreviousPeriod;
  }

  get buttonList() {
    const buttons = [
      {
        routeInfo: { name: 'group-forecaster' },
        action: 'forecast',
        disabled: !this.hasValidDependencies([PERIOD_DEPENDENCY]),
        title: this.$t('forecastDemand'),
        icon: 'mdi-poll',
        showBadge: false,
      },
      {
        routeInfo: { name: 'productRecommendation' },
        action: 'optimize',
        disabled: !this.hasValidDependencies([PERIOD_DEPENDENCY]),
        visible: this.hasValidDependencies([EXPERIMENT_DEPENDENCY]),
        title: this.$t('priceOptimizer'),
        icon: 'mdi-cash-register',
        showBadge: !!(this.currentPeriod && !this.currentPeriod.areProductPriceRecommendationsValidated && !this.isPreviousPeriod),
        isPriceButton: true
      },
      {
        routeInfo: { name: 'menu-recommendation' },
        action: 'engineer',
        disabled: !this.hasValidDependencies([PRICE_DEPENDENCY]),
        title: this.hasValidDependencies([EXPERIMENT_DEPENDENCY])
          ? this.$t('menuEngineering') : this.$t('standaloneMenuEngineering'),
        icon: 'mdi-order-bool-ascending-variant',
        showBadge: !!(this.currentPeriod && this.currentPeriod.areProductPriceRecommendationsValidated
          && !this.currentPeriod.areMenuEngineeringRecommendationsValidated && !this.isPreviousPeriod),
        isMenuEngineeringButton: true
      },
      {
        action: 'preview',
        disabled: false,
        title: this.$t('menuPreview'),
        icon: 'mdi-book-open-outline',
        showBadge: false,
      },
      {
        action: 'out-of-menu',
        disabled: false,
        title: this.$t('outOfMenu'),
        icon: 'mdi-printer',
        showBadge: false,
      },
      {
        action: 'price-change',
        disabled: false,
        title: this.$t('priceChange'),
        icon: 'mdi-file-export',
        showBadge: false,
      },
    ];

    return buttons.filter((button) => {
      if ('routeInfo' in button) {
        return PermissionController.getInstance().isAllowed(button.routeInfo.name);
      }
      return true;
    });
  }

  get emptyButtonList() {
    return this.buttonList.every((button) => button.disabled);
  }

  @Watch('predictionPeriodList')
  onPeriodChange() {
    this.showNotificationsIfNeeded();
  }

  hasValidDependencies(dependencyArr = []) {
    let valid = true;
    if (dependencyArr.includes(PERIOD_DEPENDENCY)) valid = valid && (this.predictionPeriodList.length > 0 || this.predictionPeriodList.cleared);
    if (dependencyArr.includes(PRICE_DEPENDENCY)) valid = valid && (this.currentPeriod?.areProductPriceRecommendationsValidated);
    if (dependencyArr.includes(MENU_DEPENDENCY)) valid = valid && (this.currentPeriod?.areMenuEngineeringRecommendationsValidated);
    if (dependencyArr.includes(EXPERIMENT_DEPENDENCY)) valid = valid && (!this.currentPeriod?.hasActiveExperiments);
    return valid;
  }

  showNotificationsIfNeeded() {
    if (this.emptyButtonList) {
      this.addNotification(createInfoNotification(this.$t('noManagement'), { top: true }));
    } else if (this.currentPeriod?.hasActiveExperiments) {
      this.addNotification(createInfoNotification(this.$t('noPricing'), { top: true }));
    }
  }
}

</script>
